import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Menu, Search, X } from "lucide-react";

export default function Navigation({ items }) {
  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => `${item.title} ${item.detail}`.toLowerCase().includes(term));
  }, [items, query]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const openSearch = () => {
    dialogRef.current?.showModal();
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  const closeSearch = () => {
    dialogRef.current?.close();
    setQuery("");
  };

  const goTo = (target) => {
    closeSearch();
    setMenuOpen(false);
    if (target.startsWith("/")) {
      window.location.href = target;
      return;
    }
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header className="site-nav" aria-label="Primary navigation">
        <div className="wordmark">
          <a className="wordmark__name" href="/" aria-label="Dr. Neerja Gidwani, home">Dr. Neerja</a>
          <span className="wordmark__role">Psychiatry</span>
        </div>

        <nav className="site-nav__links" aria-label="Site sections">
          <a href="/#about">About</a>
          <a href="/tools">Tools</a>
          <a href="/notes">Notes</a>
        </nav>

        <button className="search-pill" type="button" onClick={openSearch} aria-label="Search this website">
          <Search size={17} aria-hidden="true" />
          <span>Search</span>
          <kbd>⌘ K</kbd>
        </button>

        <button
          className="menu-toggle"
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>

        {menuOpen && (
          <nav className="mobile-menu" aria-label="Mobile navigation">
            <a href="/#about" onClick={() => setMenuOpen(false)}>About</a>
            <a href="/tools" onClick={() => setMenuOpen(false)}>Tools</a>
            <a href="/notes" onClick={() => setMenuOpen(false)}>Notes</a>
            <button type="button" onClick={openSearch}>Search website</button>
          </nav>
        )}
      </header>

      <dialog className="search-dialog" ref={dialogRef} onClick={(event) => {
        if (event.target === dialogRef.current) closeSearch();
      }}>
        <div className="search-dialog__surface">
          <div className="search-dialog__field">
            <Search size={20} aria-hidden="true" />
            <label className="visually-hidden" htmlFor="site-search">Search the website</label>
            <input
              id="site-search"
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try ‘visit’, ‘support’, or ‘notes’"
              autoComplete="off"
            />
            <button type="button" className="icon-button" onClick={closeSearch} aria-label="Close search">
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <p className="search-dialog__count" aria-live="polite">
            {filtered.length} {filtered.length === 1 ? "result" : "results"}
          </p>
          <div className="search-dialog__results">
            {filtered.map((item) => (
              <button key={`${item.group}-${item.title}`} type="button" onClick={() => goTo(item.target)}>
                <span>
                  <small>{item.group}</small>
                  <strong>{item.title}</strong>
                  <em>{item.detail}</em>
                </span>
                <ArrowRight size={19} aria-hidden="true" />
              </button>
            ))}
            {!filtered.length && (
              <p className="search-dialog__empty">No match yet. Try a broader word such as “support”.</p>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
}
